import { Badge, cn } from '@simplywallst/ui-core/next';
import { RouterLink } from '@redux-first-router-link';
import { DEMO_PORTFOLIO_ID } from '@/constants';
import { usePortfolio, usePortfolios } from '@/hooks/portfolio/usePortfolio';
import { useAggregatePortfolio } from '@/hooks/portfolio/useAggregatePortfolio';
import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';
import { formatterPercentage } from '@/utilities/format';
import type { ReactNode } from 'react';
import type { AggregatePortfolioFragmentType } from '@/hooks/portfolio/useAggregatePortfolio/aggregate-portfolio-fragment';
import ArrowIcon from '@simplywallst/ui-core/icons/ChevronRightSolid';
import WarningIcon from '@simplywallst/ui-core/icons/WarningSolid';

const hairSpace = ' ';

type CardProps =
  | {
      roiValue?: never;
      roiPercentage?: never;
      isPositive?: never;
      isShort?: boolean;
      isDemo?: boolean;
      count?: number;
      slug?: string;
      children: ReactNode;
    }
  | {
      roiValue: string;
      roiPercentage: string;
      isPositive?: boolean;
      isShort?: boolean;
      isDemo?: boolean;
      count?: number;
      slug?: string;
      children?: ReactNode;
    };

// TODO: Move to @simplywallst/ui-core/icons/PortfolioSection
const portfolioIcon = (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    className="-mt-0.5 inline-block size-3.5 align-middle"
  >
    <path d="M21 12c.552 0 1.005-.449.95-.998a10 10 0 0 0-8.953-8.951c-.55-.055-.998.398-.998.95v8a1 1 0 0 0 1 1z" />
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
  </svg>
);

const Card = ({
  roiValue,
  roiPercentage,
  isPositive,
  isShort,
  isDemo,
  count,
  slug,
  children,
}: CardProps) => {
  return (
    <div className="w-full @container/portfolio">
      <div
        className={cn(
          'grid grid-rows-2 items-center gap-x-2',
          '@[50ch]:grid-cols-[auto_1fr] @[50ch]:grid-rows-none',
          isShort && '@[40ch]:grid-cols-[auto_1fr] @[40ch]:grid-rows-none'
        )}
      >
        <div className="grid grid-cols-[auto_1fr] items-center gap-1 text-softer">
          {portfolioIcon}
          {(count ?? 0) > 1
            ? `${count} Portfolio Position`
            : 'Portfolio Position'}
        </div>
        <div className="flex w-full items-center justify-between gap-inherit">
          <div className="flex place-items-center gap-inherit whitespace-nowrap">
            {children ?? (
              <>
                <span
                  className="whitespace-nowrap"
                  title="Return on investment"
                >
                  {roiValue}
                </span>
                <span
                  className={cn(
                    'flex whitespace-nowrap',
                    isPositive ? 'text-good' : 'text-bad'
                  )}
                  title="Return percentage"
                >
                  {isPositive ? '↑' : '↓'}
                  {hairSpace}
                  {roiPercentage}
                </span>
                {isDemo && <Badge className="min-w-fit">Demo</Badge>}
              </>
            )}
          </div>
          <RouterLink
            to={slug}
            className="relative grid grid-cols-[1fr_auto] items-center gap-1 whitespace-nowrap text-brand-01 no-underline duration-75 hover:text-p-white"
            onClick={() => {
              // TODO: Add tracking
            }}
          >
            View
            <ArrowIcon className="h-3 w-auto fill-current" />
            {/* Larger hit area */}
            <div className="absolute -inset-2 opacity-0" />
          </RouterLink>
        </div>
      </div>
    </div>
  );
};

const PortfolioCard = ({
  data,
  count,
  slug,
  isPositive = true,
  children,
}: {
  data?: AggregatePortfolioFragmentType;
  count: number;
  slug: string;
  isPositive?: boolean;
  children?: ReactNode;
}) => {
  const roiValue = formatCurrencyToScaleTruncated(
    data?.analysis.roiValue,
    data?.analysis.currencyISO ?? undefined
  );
  const roiPercentage = formatterPercentage(data?.analysis.roi ?? 0);

  return (
    <Card
      roiValue={roiValue}
      roiPercentage={roiPercentage}
      isPositive={isPositive}
      count={count}
      slug={slug}
    >
      {children}
    </Card>
  );
};

const Layout = ({
  isPositive,
  children,
}: {
  isPositive?: boolean;
  children: ReactNode;
}) => {
  return (
    <section className="relative h-full">
      {/* Large backgrounds */}
      <div className="pointer-events-none absolute inset-0 -right-x4 overflow-hidden max-md:hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-[rgba(255,255,255,.01)]" />
        {isPositive !== undefined && (
          <div
            className={cn(
              'absolute size-full translate-y-1/2',
              'bg-[radial-gradient(var(--tw-gradient-from),var(--tw-gradient-to)_50%)] to-transparent',
              isPositive ? 'from-good-subtle' : 'from-bad-subtle'
            )}
          />
        )}
      </div>
      {/* Small backgrounds */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden md:hidden">
        <div className="border absolute inset-0 rounded-md bg-legacy-noir/65" />
        {isPositive !== undefined && (
          <div
            className={cn(
              'absolute size-full translate-y-1/2',
              'bg-[radial-gradient(var(--tw-gradient-from),var(--tw-gradient-to)_50%)] to-transparent',
              isPositive ? 'from-good-subtle' : 'from-bad-subtle'
            )}
          />
        )}
      </div>

      <div className="relative flex h-full items-center justify-between gap-2 text-xs max-md:px-x2">
        {children}
      </div>
    </section>
  );
};

const DemoCard = () => {
  const demo = usePortfolio({ id: DEMO_PORTFOLIO_ID });
  const roiValue = formatCurrencyToScaleTruncated(demo.data?.analysis.roiValue);
  const roiPercentage = formatterPercentage(demo.data?.analysis.roi ?? 0);
  const isPositive = demo.data?.analysis.roi
    ? demo.data?.analysis.roi > 0
    : true;

  return (
    <Layout isPositive={isPositive}>
      <Card
        roiValue={roiValue}
        roiPercentage={roiPercentage}
        isPositive={isPositive}
        isDemo
      />
    </Layout>
  );
};

export const PortfolioPositionCard = () => {
  const { isFetched: isFetchedSummary, data: summary } =
    useAggregatePortfolio();
  const { isFetched: portfoliosIsFetched, data: portfolios = [] } =
    usePortfolios();

  // TODO: Improve loader
  // TODO: i18n
  if (!isFetchedSummary) return <>Loading...</>;
  if (!portfoliosIsFetched) return <>Loading...</>;

  if (portfolios.length === 0) return <DemoCard />;

  const hasNoHoldings = summary?.analysis.holdingsCount === 0;
  const hasIncompleteData = summary?.analysis.roiValue === null;
  const slug =
    portfolios.length === 1 ? `/portfolio/${portfolios[0].id}` : '/portfolio';

  if (hasIncompleteData)
    return (
      <Layout>
        <Card slug={slug} isShort>
          <div>
            <RouterLink
              to={slug}
              className="grid grid-cols-[auto_1fr] items-center gap-1 text-current no-underline"
              onClick={() => {
                // TODO: Add Tracking
              }}
            >
              <WarningIcon className="-mt-px inline-block size-5 fill-warning" />
              {/* TODO: i18n */}
              Missing data
            </RouterLink>
          </div>
        </Card>
      </Layout>
    );

  if (hasNoHoldings)
    return (
      <Layout>
        {/* TODO: i18n */}
        <Card slug={slug}>No holdings</Card>
      </Layout>
    );

  const isPositive = (summary?.analysis.roi ?? 0) > 0;
  return (
    <Layout isPositive={isPositive}>
      <PortfolioCard
        data={summary}
        slug={slug}
        isPositive={isPositive}
        count={portfolios.length}
      />
    </Layout>
  );
};
